import { ReferralProduct } from "@redotech/crdb-enums/referrals/referral-product";
import { ReferrerAccountStatus } from "@redotech/crdb-enums/referrals/referrer-account-status";
import { ReferrerAccountType } from "@redotech/crdb-enums/referrals/referrer-account-type";
import * as z from "zod";

export type ReferrerStatus = `${ReferrerAccountStatus}`;
export const referrerStatusSchema: z.ZodType<ReferrerStatus, ReferrerStatus> =
  z.enum(ReferrerAccountStatus);

export type ReferrerType = `${ReferrerAccountType}`;
export const referrerTypeSchema: z.ZodType<ReferrerType, ReferrerType> =
  z.enum(ReferrerAccountType);

export type ReferrerProductCode = `${ReferralProduct}`;
const referrerProductCodeSchema: z.ZodType<
  ReferrerProductCode,
  ReferrerProductCode
> = z.enum(ReferralProduct);

const referrerInstallOrderVolumeSchema = z.object({
  recordedAt: z.iso.datetime(),
  last30Days: z.number().int().min(0).optional(),
  lastYear: z.number().int().min(0).optional(),
});
export type ReferrerInstallOrderVolume = z.infer<
  typeof referrerInstallOrderVolumeSchema
>;

const referrerInstallMetricsSnapshotSchema = z.object({
  onlineStoreOrderVolume: referrerInstallOrderVolumeSchema.optional(),
  totalOrderVolume: referrerInstallOrderVolumeSchema.optional(),
});
export type ReferrerInstallMetricsSnapshot = z.infer<
  typeof referrerInstallMetricsSnapshotSchema
>;

export const referrerInstallSchema = z.object({
  id: z.string(),
  shopName: z.string(),
  shopDomain: z.string(),
  teamId: z.string().nullable(),
  metricsSnapshot: referrerInstallMetricsSnapshotSchema.nullable(),
  productCode: referrerProductCodeSchema,
  installedAt: z.iso.datetime(),
});
export type ReferrerInstall = z.infer<typeof referrerInstallSchema>;

/**
 * Shape returned by list-referrers and used as the row type in tables.
 */
export const referrerSummarySchema = z.object({
  id: z.string(),
  email: z.string(),
  name: z.string(),
  referralCode: z.string(),
  profileImageUrl: z.string().url().nullable(),
  referrerType: referrerTypeSchema,
  status: referrerStatusSchema,
  installCount: z.number().int().min(0),
  createdBy: z.string(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type ReferrerSummary = z.infer<typeof referrerSummarySchema>;

/**
 * Auth-event row included in referrer detail views. Free-form `eventType`
 * because new types may be added (admin-side audits, social-login, etc.)
 * without forcing a schema bump on every consumer.
 */
const referrerAuthEventSchema = z.object({
  id: z.string(),
  referrerId: z.string().nullable(),
  email: z.string().nullable(),
  eventType: z.string(),
  ipAddress: z.string().nullable(),
  userAgent: z.string().nullable(),
  magicLinkJti: z.string().nullable(),
  occurredAt: z.string(),
});
export type ReferrerAuthEvent = z.infer<typeof referrerAuthEventSchema>;

/**
 * Shape returned by get-referrer. Includes the most-recent auth events
 * so the detail page can render them inline without a follow-up RPC.
 */
export const referrerDetailSchema = referrerSummarySchema.extend({
  recentAuthEvents: z.array(referrerAuthEventSchema),
});
export type ReferrerDetail = z.infer<typeof referrerDetailSchema>;
